/**
 * Scoped Access API client (Org Architecture Phase G, spec §3 API).
 *
 * Mirrors apps/chaos-gateway/src/access-grants.ts. All routes are
 * owner/admin-gated server-side. The screens that use this degrade gracefully
 * on a 404 (gateway route absent / older build) so a UI/gateway flag mismatch
 * never hard-errors — callers catch ApiError(status 404) and show the OFF state.
 */
import type {
  AccessGrant,
  CreateAccessGrantInput,
  DelegateGrantInput,
  EffectiveAccess,
  EffectiveAccessTestQueryResult,
  GovernanceMember,
} from '@chaos/types/orgs';

import { apiJson } from './client';

export interface AccessAuditEntry {
  id: string;
  actorKind: 'user' | 'agent' | 'system';
  actorUserId: string | null;
  actorRoleId: string | null;
  actingAsUserId: string | null;
  action: 'access_check_allowed' | 'access_check_denied' | 'grant_added' | 'grant_removed' | 'grant_updated';
  detail: Record<string, unknown> | null;
  createdAt: string | null;
}

export const accessKeys = {
  grants: (orgId: string) => ['access', 'grants', orgId] as const,
  governance: (orgId: string) => ['access', 'governance', orgId] as const,
  effective: (orgId: string, kind: string, id: string) =>
    ['access', 'effective', orgId, kind, id] as const,
  audit: (orgId: string) => ['access', 'audit', orgId] as const,
};

/** P6 §6.4 — the inspector's test-query input. The PATH is derived server-side
 *  from `cardId` (optional); the caller never supplies scope ids. */
export interface AccessTestQueryInput {
  subjectKind: 'human' | 'agent';
  subjectId: string;
  query: string;
  cardId?: string | null;
  k?: number;
}

export const accessApi = {
  listGrants(orgId: string): Promise<AccessGrant[]> {
    return apiJson<{ grants: AccessGrant[] }>(`/orgs/${orgId}/access/grants`).then(
      (r) => r.grants,
    );
  },
  grant(orgId: string, input: CreateAccessGrantInput): Promise<AccessGrant> {
    return apiJson<{ grant: AccessGrant }>(`/orgs/${orgId}/access/grants`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revoke(orgId: string, grantId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/orgs/${orgId}/access/grants/${grantId}`, {
      method: 'DELETE',
    });
  },
  effective(
    orgId: string,
    subjectKind: 'human' | 'agent',
    subjectId: string,
  ): Promise<EffectiveAccess> {
    return apiJson<{ effective: EffectiveAccess }>(
      `/orgs/${orgId}/access/effective?subjectKind=${subjectKind}&subjectId=${subjectId}`,
    ).then((r) => r.effective);
  },
  governance(orgId: string): Promise<GovernanceMember[]> {
    return apiJson<{ members: GovernanceMember[] }>(
      `/orgs/${orgId}/access/governance`,
    ).then((r) => r.members);
  },
  audit(orgId: string): Promise<AccessAuditEntry[]> {
    return apiJson<{ entries: AccessAuditEntry[] }>(`/orgs/${orgId}/access/audit`).then(
      (r) => r.entries,
    );
  },
  // P6 §6.4 — run a kb_search-style TEST QUERY as a subject (the inspector).
  testQuery(
    orgId: string,
    input: AccessTestQueryInput,
  ): Promise<EffectiveAccessTestQueryResult> {
    const params = new URLSearchParams({
      subjectKind: input.subjectKind,
      subjectId: input.subjectId,
      q: input.query,
    });
    if (input.cardId) params.set('cardId', input.cardId);
    if (input.k) params.set('k', String(input.k));
    return apiJson<{ result: EffectiveAccessTestQueryResult }>(
      `/orgs/${orgId}/access/effective/test-query?${params.toString()}`,
    ).then((r) => r.result);
  },
  // P6 §6.2 — in-session DELEGATION: lend an agent a temporary read-only grant.
  delegate(orgId: string, input: DelegateGrantInput): Promise<AccessGrant> {
    return apiJson<{ grant: AccessGrant }>(`/orgs/${orgId}/access/grants/delegate`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  // P6 §6.1 — set/clear a member's can_delegate flag (founder/admin only).
  setCanDelegate(
    orgId: string,
    userId: string,
    canDelegate: boolean,
  ): Promise<{ ok: boolean; userId: string; canDelegate: boolean }> {
    return apiJson<{ ok: boolean; userId: string; canDelegate: boolean }>(
      `/orgs/${orgId}/members/${userId}/can-delegate`,
      { method: 'PATCH', json: { canDelegate } },
    );
  },
};
